import React from 'react'
import { Box, Typography, IconButton, Grid } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonIcon from '@mui/icons-material/Person'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import BadgeIcon from '@mui/icons-material/Badge'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import AssignmentIcon from '@mui/icons-material/Assignment'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import { EPatientStatus } from '../RoomView.types'
import {
  getAgeFromDate,
  getDurationBetweenDates,
  getProviderName,
  getStatusLabel,
  getTimeFromDate,
} from '../helpers'

import { TPatientManagementModalProps } from './PatientManagementModal.types'
import { usePatientManagementModalLogic } from './PatientManagementModal.logic'
import {
  ActionButton,
  ActionsContainer,
  InfoRow,
  InfoSection,
  InfoValue,
  LabelWithMargin,
  ProcedureChip,
  StyledDialog,
  StyledDialogContent,
  StyledDialogTitle,
  StyledMenu,
  StyledMenuItem,
  TimelinePill,
  TimelineScroller,
  Label,
  StatusLabel,
  StatusTime,
} from './PatientManagementModal.styles'
import { formatUSPhoneNumberForDisplay } from 'common/utils'

const PatientManagementModal: React.FC<TPatientManagementModalProps> = (
  props
) => {
  const { open, onClose, patient } = props
  const logic = usePatientManagementModalLogic(props)

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <StyledDialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon color="primary" />
          <Typography variant="h6" component="div" fontWeight={600} noWrap>
            {patient.firstName} {patient.lastName}
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <InfoSection>
              <Typography variant="subtitle2" color="primary" fontWeight={600}>
                Patient Information
              </Typography>
              <InfoRow>
                <Label>
                  <BadgeIcon fontSize="small" />
                  MRN
                </Label>
                <InfoValue>{patient.idMR}</InfoValue>
              </InfoRow>
              <InfoRow>
                <Label>
                  <CalendarMonthIcon fontSize="small" />
                  DOB
                </Label>
                <InfoValue>
                  {`${patient.dateOfBirth} (${getAgeFromDate(
                    patient.dateOfBirth
                  )} years)`}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <Label>
                  <PhoneIcon fontSize="small" />
                  Phone
                </Label>
                <InfoValue>
                  {patient?.phoneNumber
                    ? formatUSPhoneNumberForDisplay(patient.phoneNumber)
                    : 'N/A'}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <Label>
                  <EmailIcon fontSize="small" />
                  Email
                </Label>
                <InfoValue>{patient?.email ? patient.email : 'N/A'}</InfoValue>
              </InfoRow>
            </InfoSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <InfoSection>
              <Typography variant="subtitle2" color="primary" fontWeight={600}>
                Procedure Information
              </Typography>
              <InfoRow>
                <Label>
                  <LocalHospitalIcon fontSize="small" />
                  Surgeon
                </Label>
                <InfoValue>
                  {getProviderName(logic.surgery?.attendingProvider)}
                </InfoValue>
              </InfoRow>
              {logic.surgery && (
                <InfoRow>
                  <Label>
                    <AccessTimeIcon fontSize="small" />
                    Time
                  </Label>
                  <InfoValue>{logic.time}</InfoValue>
                </InfoRow>
              )}
              {logic.surgery?.procedure && (
                <Box>
                  <LabelWithMargin>
                    <AssignmentIcon fontSize="small" />
                    Procedures
                  </LabelWithMargin>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    <ProcedureChip
                      label={logic.surgery.procedure.description}
                      color="primary"
                    />
                  </Box>
                </Box>
              )}
            </InfoSection>
          </Grid>

          <Grid item xs={12}>
            <InfoSection>
              <Typography variant="subtitle2" color="primary" fontWeight={600}>
                Status Timeline
              </Typography>
              <TimelineScroller>
                {logic.surgery?.patientStatusHistory
                  ?.slice()
                  .reverse()
                  .map((item, index, reversedArray) => (
                    <TimelinePill
                      key={index}
                      active={
                        item.status ===
                          logic.surgery?.patientStatusHistory[0]?.status &&
                        item.info?.createdAt ===
                          logic.surgery?.patientStatusHistory[0]?.info
                            ?.createdAt
                      }
                    >
                      <StatusLabel>
                        {`${getStatusLabel(
                          item.status,
                          logic.patientStatusOptions
                        ).toUpperCase()} ${
                          item.status === EPatientStatus.ASSIGN_ROOM
                            ? `(${item.info?.room?.name})`
                            : ''
                        }`}
                      </StatusLabel>
                      <StatusTime>
                        {`${getTimeFromDate(item.info?.createdAt)}${
                          reversedArray && index < reversedArray.length - 1
                            ? ` - ${getTimeFromDate(
                                reversedArray[index + 1]?.info?.createdAt
                              )} (${getDurationBetweenDates(
                                item.info?.createdAt,
                                reversedArray[index + 1]?.info?.createdAt
                              )})`
                            : ''
                        }`}
                      </StatusTime>
                    </TimelinePill>
                  ))}
              </TimelineScroller>
            </InfoSection>
          </Grid>

          {patient?.referringPhysician && (
            <Grid item xs={12}>
              <InfoSection>
                <Typography
                  variant="subtitle2"
                  color="primary"
                  fontWeight={600}
                >
                  Referring Physician
                </Typography>
                <InfoRow>
                  <Label>
                    <PersonIcon fontSize="small" />
                    Name
                  </Label>
                  <InfoValue>
                    {patient.referringPhysician?.lastName},{' '}
                    {patient.referringPhysician?.firstName[0]}
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <Label>
                    <PhoneIcon fontSize="small" />
                    Phone
                  </Label>
                  <InfoValue>
                    {patient.referringPhysician?.mobile
                      ? formatUSPhoneNumberForDisplay(
                          patient.referringPhysician.mobile
                        )
                      : 'N/A'}
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <Label>
                    <EmailIcon fontSize="small" />
                    Email
                  </Label>
                  <InfoValue>
                    {patient.referringPhysician?.email
                      ? patient.referringPhysician.email
                      : 'N/A'}
                  </InfoValue>
                </InfoRow>
              </InfoSection>
            </Grid>
          )}
        </Grid>

        <StyledMenu
          anchorEl={logic.roomMenuAnchorEl}
          open={!!logic.roomMenuAnchorEl}
          onClose={() => logic.setRoomMenuAnchorEl(null)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transitionDuration={200}
          elevation={0}
        >
          {logic.rooms.map((room) => {
            const isRoomOccupied = logic.scheduledSurgeries.some(
              (surgery) =>
                surgery?.patientStatusHistory[0]?.info?.room?.id === room.id
            )
            return (
              <StyledMenuItem
                key={room.id}
                onClick={() => logic.handleMoveToRoom(room)}
                disabled={isRoomOccupied}
              >
                {room.name}
              </StyledMenuItem>
            )
          })}
        </StyledMenu>

        <ActionsContainer>
          {logic.surgery?.patientStatusHistory[0]?.status ===
            EPatientStatus.ASSIGN_ROOM && (
            <ActionButton
              variant="contained"
              color="primary"
              size="small"
              startIcon={<LocalHospitalIcon />}
              onClick={(e) => logic.setRoomMenuAnchorEl(e.currentTarget)}
            >
              Move to Room
            </ActionButton>
          )}
          {/* <ActionButton
            variant="outlined"
            size="small"
            startIcon={<HistoryIcon />}
          >
            View History
          </ActionButton> */}
        </ActionsContainer>
      </StyledDialogContent>
    </StyledDialog>
  )
}

export default PatientManagementModal
